import { useCallback, useMemo } from 'react';
import { getRouteApi } from '@tanstack/react-router';
import { useUrlSyncedFilters } from '@/hooks/useUrlSyncedFilters';
import { getMonthToDateRange } from '@/utils/date';
import { DATE_RANGE_FILTER_KEYS } from '@/utils/filterFields';
import type { MpRegionSearch } from '../types/marketPromotion.types';
import { toMpRegionFilterSearch, toMpRegionFilterValues } from '../utils/mpFilters';

const route = getRouteApi('/_auth/market-promotion/region-summary');

export function useMpRegionFilters() {
  const search = route.useSearch();
  const navigate = route.useNavigate();

  const defaultPeriod = useMemo(() => getMonthToDateRange(), []);
  const appliedValues = useMemo(
    () => toMpRegionFilterValues(search, defaultPeriod),
    [search, defaultPeriod],
  );

  const normalizeFilterValues = useCallback(
    (values: Record<string, string>) =>
      toMpRegionFilterValues(toMpRegionFilterSearch(values), defaultPeriod),
    [defaultPeriod],
  );

  const filters = useUrlSyncedFilters({
    appliedValues,
    defaultValues: defaultPeriod,
    rangeKeys: DATE_RANGE_FILTER_KEYS,
    normalizeValues: normalizeFilterValues,
    onApply: (values) => {
      void navigate({
        search: (previous) => ({ ...previous, ...toMpRegionFilterSearch(values) }),
        replace: true,
      });
    },
  });

  const params = useMemo(
    (): MpRegionSearch & { dateFrom: string; dateTo: string } => ({
      ...search,
      dateFrom: search.dateFrom ?? defaultPeriod.dateFrom,
      dateTo: search.dateTo ?? defaultPeriod.dateTo,
    }),
    [search, defaultPeriod],
  );

  return { ...filters, params };
}
